import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import DataSourceBadge from '../components/DataSourceBadge';

export default function TrafficAnalyticsPage() {
  const [params] = useSearchParams();
  const [domain, setDomain] = useState(params.get('domain') || '');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const { updateCredits } = useAuth();

  async function analyze() {
    if (!domain.trim() || loading) return;
    setError(''); setLoading(true); setData(null);
    try {
      const res = await api.post('/domain/traffic', { domain });
      setData(res.data.data);
      updateCredits(res.data.credits_remaining);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); } finally { setLoading(false); }
  }

  useEffect(() => { if (params.get('domain')) analyze(); }, []);

  return (
    <div className="fade-up">
      <div className="page-header">
        <h1 className="page-title">Traffic Analytics</h1>
        <p className="page-sub">Estimated organic traffic from search · 3 credits</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="input input-lg" placeholder="Enter domain e.g. amazon.com" value={domain} onChange={e => setDomain(e.target.value)} onKeyDown={e => e.key === 'Enter' && analyze()} />
          <button className="btn btn-orange btn-lg" onClick={analyze} disabled={loading || !domain.trim()}>{loading ? 'Loading...' : 'Analyze →'}</button>
        </div>
        {error && <div style={{ marginTop: 10, color: '#FF3D57', fontSize: 13, background: '#FFEBEE', padding: '8px 12px', borderRadius: 6 }}>{error}</div>}
      </div>

      {loading && <div className="card"><div className="skeleton" style={{ height: 200 }} /></div>}

      {data && (
        <>
          <div style={{ marginBottom: 12 }}>
            <DataSourceBadge isReal={data.isReal} dataSource={data.dataSource} />
          </div>

          <div className="metric-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="card" style={{ margin: 0 }}>
              <div className="metric-label">Estimated organic search traffic</div>
              <div className="metric-value">{data.totalVisitsFormatted ?? (data.totalVisits/1000).toFixed(1) + 'K'}</div>
              <div className="metric-sub">monthly, from search rankings — this is the only figure with real underlying data on this page</div>
            </div>
          </div>

          {!data.channelDataAvailable && (
            <div className="card" style={{ marginBottom: 16, background: '#FFF8E1', border: '1px solid #FFE082' }}>
              <div style={{ fontSize: 13, color: '#8B5E00', fontWeight: 600, marginBottom: 4 }}>Per-channel traffic not available</div>
              <div style={{ fontSize: 12, color: '#8B5E00' }}>{data.channelDataNote}</div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <span className="card-title">Top Countries</span>
              <span className="card-subtitle">Illustrative distribution — not a measured geo-traffic source</span>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Country</th><th>Share</th><th>Traffic</th></tr></thead>
                <tbody>
                  {(data.topCountries || []).map(c => (
                    <tr key={c.code}><td><strong>{c.code}</strong> {c.country}</td><td>{c.share}%</td><td>{(c.traffic/1000).toFixed(1)}K</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
